import { type TopicIdentityRecord } from "@/shared/domain/topics";
import {
  HttpClient,
  type PaginatedResponse,
  robotoHeaders,
} from "@/shared/http";
import { bigintReplacer, parseTimestampsAsBigInt } from "@/shared/json";

import { type SessionFile } from "./SessionFile";
import {
  type CreateSessionRequest,
  type SessionFileContribution,
  type SessionRecord,
  type UpdateSessionRequest,
} from "./SessionRecord";

/**
 * Options for requests to `/v1/sessions/id/<id>`. The server authorizes these
 * against the org that owns the session, so they carry no org header.
 */
interface RequestOptions {
  abortSignal?: AbortSignal;
}

interface ListOptions extends RequestOptions {
  pageToken?: string;
}

interface CreateSessionOptions {
  abortSignal?: AbortSignal;
  /**
   * Sets the `X-Roboto-Org-Id` header. Required for callers whose Cognito
   * identity is a member of more than one org: the backend
   * `REQUIRES_CALLER_ORG` rule rejects ambiguous requests with
   * `RobotoNoOrgProvidedException`.
   */
  orgId?: string;
}

/**
 * HTTP client for the `/v1/sessions` API. Construct once per
 * `HttpClient`; resolved by feature code through the
 * `DomainServicesProvider` context.
 */
export class SessionService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * Attach file contributions to an existing session via
   * `POST /v1/sessions/id/<id>/files`, returning the updated `SessionRecord`
   * whose aggregate bounds reflect the added contributions. Re-adding a file
   * already in the session replaces its range.
   */
  public async addFilesToSession(
    sessionId: string,
    files: SessionFileContribution[],
    options?: RequestOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/sessions/id/${sessionId}/files`,
    );
    // The server upserts re-added files, so a retried request converges.
    const response = await this.#httpClient.post(url, {
      idempotent: true,
      // bigintReplacer keeps ns-epoch range bounds full-precision on the wire.
      body: JSON.stringify({ files }, bigintReplacer),
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }

  /**
   * Create a session via `POST /v1/sessions`. The session starts with no
   * file contributions; attach them in a follow-up {@link addFilesToSession}
   * call.
   * `useCreateSessionFromFiles` chains the two with delete-on-failure
   * rollback.
   */
  public async createSession(
    request: CreateSessionRequest,
    options?: CreateSessionOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl("v1/sessions");
    // Non-idempotent on the server (each POST mints a new session_id), so a
    // transient 502 must not be silently retried into a duplicate session.
    const response = await this.#httpClient.post(url, {
      idempotent: false,
      headers: options?.orgId
        ? robotoHeaders({ orgId: options.orgId })
        : undefined,
      body: JSON.stringify(request),
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }

  public async deleteSession(
    sessionId: string,
    options?: RequestOptions,
  ): Promise<void> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.delete(url, {
      idempotent: false,
      signal: options?.abortSignal,
    });
    await response.throwIfError();
  }

  public async getSessionById(
    sessionId: string,
    options?: RequestOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }

  /**
   * List every file in a session, following `next_token` until the server
   * reports no more pages.
   *
   * `limit` sets the page size for each request in the chain; the route
   * defaults to 100 and rejects a page size above 1000.
   */
  public async listAllSessionFiles(
    sessionId: string,
    options?: Omit<ListOptions, "pageToken"> & { limit?: number },
  ): Promise<SessionFile[]> {
    const allFiles: SessionFile[] = [];
    let pageToken: string | undefined = undefined;

    do {
      const page = await this.listSessionFiles(sessionId, {
        ...options,
        pageToken,
      });
      allFiles.push(...page.items);
      pageToken = page.next_token ?? undefined;
    } while (pageToken);

    return allFiles;
  }

  /**
   * List the IDs of every device attached to a session: devices attached
   * directly, plus those the backend infers from the `device_id` on the
   * session's files or their datasets. Follows `next_token` until the
   * server reports no more pages.
   */
  public async listAllSessionDevices(
    sessionId: string,
    options?: Omit<ListOptions, "pageToken">,
  ): Promise<string[]> {
    const allDeviceIds: string[] = [];
    let pageToken: string | undefined = undefined;

    do {
      const searchParams = new URLSearchParams();
      if (pageToken !== undefined) {
        searchParams.append("page_token", pageToken);
      }
      const url = this.#httpClient.constructUrl(
        `v1/sessions/id/${sessionId}/devices`,
        searchParams,
      );
      const response = await this.#httpClient.get(url, {
        signal: options?.abortSignal,
      });
      const page = await response.json<PaginatedResponse<string>>();
      allDeviceIds.push(...page.items);
      pageToken = page.next_token ?? undefined;
    } while (pageToken);

    return allDeviceIds;
  }

  public async listSessionFiles(
    sessionId: string,
    options?: ListOptions & { limit?: number },
  ): Promise<PaginatedResponse<SessionFile>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    if (options?.limit !== undefined) {
      searchParams.append("page_size", String(options.limit));
    }
    const url = this.#httpClient.constructUrl(
      `v1/sessions/id/${sessionId}/files`,
      searchParams,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<SessionFile>>(
      parseTimestampsAsBigInt,
    );
  }

  public async listSessionTopics(
    sessionId: string,
    options?: ListOptions,
  ): Promise<PaginatedResponse<TopicIdentityRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    const url = this.#httpClient.constructUrl(
      `v1/sessions/id/${sessionId}/topics`,
      searchParams,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<TopicIdentityRecord>>();
  }

  public async updateSession(
    sessionId: string,
    updates: UpdateSessionRequest,
    options?: RequestOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.put(url, {
      idempotent: true,
      body: JSON.stringify(updates),
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }
}
